import { useState } from "react";
import { ChevronDown, ChevronRight, Clock } from "lucide-react";
import { format } from "date-fns";
import { useLabelPrintLog } from "@/hooks/usePrintLog";

interface PrintHistoryProps {
  labelId: string | null | undefined;
}

export function PrintHistory({ labelId }: PrintHistoryProps) {
  const [open, setOpen] = useState(false);
  const { data: logs = [], isLoading } = useLabelPrintLog(labelId);

  if (!labelId) return null;

  const total = logs.reduce((s, l) => s + l.quantity, 0);
  const lastLog = logs[0];

  return (
    <div className="border-t mt-1">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-1.5 px-2.5 py-2 text-left hover:bg-muted/40 rounded transition-colors"
      >
        <Clock className="h-3 w-3 text-muted-foreground shrink-0" />
        <span className="text-[11px] font-medium text-muted-foreground flex-1">Print history</span>
        {total > 0 && (
          <span className="text-[10px] text-muted-foreground/70 shrink-0">
            {total.toLocaleString()} total
          </span>
        )}
        {open
          ? <ChevronDown  className="h-3 w-3 text-muted-foreground/60 shrink-0" />
          : <ChevronRight className="h-3 w-3 text-muted-foreground/60 shrink-0" />
        }
      </button>

      {/* Collapsed summary */}
      {!open && lastLog && (
        <p className="text-[10px] text-muted-foreground/60 px-2.5 pb-1.5 -mt-1">
          Last: {format(new Date(lastLog.printed_at), "MMM d, yyyy")} · {lastLog.quantity} labels
        </p>
      )}

      {/* Expanded log */}
      {open && (
        <div className="px-2.5 pb-2 space-y-0.5">
          {isLoading ? (
            <p className="text-[11px] text-muted-foreground py-1">Loading…</p>
          ) : logs.length === 0 ? (
            <p className="text-[11px] text-muted-foreground py-1">No prints recorded yet.</p>
          ) : (
            logs.map((log) => (
              <div key={log.id} className="flex items-center justify-between py-0.5">
                <span className="text-[11px] text-muted-foreground">
                  {format(new Date(log.printed_at), "MMM d, yyyy · h:mm a")}
                </span>
                <span className="text-[11px] font-medium tabular-nums">
                  {log.quantity.toLocaleString()} labels
                </span>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
